import * as THREE from "three"
import { FEATURED_PROJECTS } from "./projects"

export type DestinationId =
  | "engineering"
  | "ai"
  | "creative"
  | "music"

export type Destination = {
  id: DestinationId
  label: string
  blurb: string
  position: THREE.Vector3
  color: number
}

export const DESTINATIONS: Destination[] = [
  {
    id: "engineering",
    label: "ENGINEERING",
    blurb: "Systems, platforms, and durable infrastructure.",
    position: new THREE.Vector3(-8, 0, -95),
    color: 0x3a8dff,
  },
  {
    id: "ai",
    label: "ARTIFICIAL INTELLIGENCE",
    blurb: "Agents, models, and human-centered automation.",
    position: new THREE.Vector3(12, 2, -125),
    color: 0xb44dff,
  },
  {
    id: "creative",
    label: "CREATIVE TECHNOLOGY",
    blurb: "Interfaces where craft meets code.",
    position: new THREE.Vector3(-10, 4, -155),
    color: 0xff6a3d,
  },
  {
    id: "music",
    label: "MUSIC AND SOUND",
    blurb: "Studios, instruments, and living audio worlds.",
    position: new THREE.Vector3(10, -1, -185),
    color: 0x2ee6c5,
  },
]

export type PlanetSystem = {
  group: THREE.Group
  destinations: Destination[]
  setVisibility: (v: number) => void
  nearest: (cam: THREE.Vector3) => Destination | null
  update: (time: number) => void
  dispose: () => void
}

function makePlanet(
  radius: number,
  color: number,
  segments: number,
  emissive = 0x000000,
): THREE.Mesh {
  const mat = new THREE.MeshStandardMaterial({
    color,
    metalness: 0.15,
    roughness: 0.65,
    emissive,
    emissiveIntensity: emissive ? 0.45 : 0,
  })
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, segments, segments), mat)
  return mesh
}

export function createPlanets(segments: number): PlanetSystem {
  const group = new THREE.Group()
  group.name = "planets"
  group.visible = false

  const bodies: THREE.Object3D[] = []

  DESTINATIONS.forEach((d, i) => {
    const root = new THREE.Group()
    root.position.copy(d.position)
    const planet = makePlanet(7.5 + (i % 3) * 1.4, d.color, segments, d.color)
    root.add(planet)

    const planetRadius = (planet.geometry as THREE.SphereGeometry).parameters.radius
    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(
        planetRadius * 1.08,
        Math.max(16, Math.floor(segments / 2)),
        Math.max(16, Math.floor(segments / 2)),
      ),
      new THREE.MeshBasicMaterial({
        color: d.color,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.BackSide,
      }),
    )
    root.add(atmosphere)

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(11 + i * 0.6, 0.14, 8, 64),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    ring.rotation.x = Math.PI * (0.4 + i * 0.08)
    root.add(ring)

    const station = new THREE.Mesh(
      new THREE.BoxGeometry(1.8, 0.45, 1.8),
      new THREE.MeshStandardMaterial({
        color: 0xccd8ff,
        emissive: d.color,
        emissiveIntensity: 1.4,
        metalness: 0.6,
        roughness: 0.3,
        toneMapped: false,
      }),
    )
    station.position.set(12, 2, 0)
    root.add(station)

    const beacon = new THREE.PointLight(d.color, 2.4, 80, 2)
    beacon.position.set(0, 0, 0)
    root.add(beacon)

    group.add(root)
    bodies.push(root)
  })

  // Distant sun
  const sun = new THREE.Mesh(
    new THREE.SphereGeometry(8, 32, 32),
    new THREE.MeshBasicMaterial({ color: 0xffc58a }),
  )
  sun.position.set(60, 30, -200)
  const sunLight = new THREE.PointLight(0xffb070, 2.2, 400, 2)
  sunLight.position.copy(sun.position)
  group.add(sun, sunLight)

  // Floating project screens (world space, face camera each frame)
  const showcases: THREE.Mesh[] = []
  const loader = new THREE.TextureLoader()
  for (const project of FEATURED_PROJECTS) {
    const dest = DESTINATIONS.find((d) => d.id === project.destinationId)
    if (!dest) continue
    const tex = loader.load(project.image)
    tex.colorSpace = THREE.SRGBColorSpace
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(14, 7.4),
      new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: 0.95,
        side: THREE.DoubleSide,
      }),
    )
    panel.position.set(dest.position.x + 10, dest.position.y + 4, dest.position.z + 6)
    // Soft frame glow
    const frame = new THREE.Mesh(
      new THREE.PlaneGeometry(14.4, 7.8),
      new THREE.MeshBasicMaterial({
        color: dest.color,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    )
    frame.position.copy(panel.position)
    frame.position.z -= 0.05
    group.add(frame, panel)
    showcases.push(panel, frame)
  }

  return {
    group,
    destinations: DESTINATIONS,
    setVisibility(v) {
      const visibility = THREE.MathUtils.clamp(v, 0, 1)
      group.visible = visibility > 0.02
      group.scale.setScalar(0.85 + visibility * 0.15)
    },
    nearest(cam) {
      let best: Destination | null = null
      let bestD = 48
      for (const d of DESTINATIONS) {
        const dist = cam.distanceTo(d.position)
        if (dist < bestD) {
          bestD = dist
          best = d
        }
      }
      return best
    },
    update(time) {
      bodies.forEach((b, i) => {
        b.rotation.y = time * (0.05 + i * 0.01)
        b.position.y = DESTINATIONS[i].position.y + Math.sin(time * 0.4 + i) * 0.35
      })
      // Billboard project panels toward origin flight path (approx camera)
      for (const panel of showcases) {
        panel.lookAt(0, panel.position.y, panel.position.z + 40)
      }
    },
    dispose() {
      group.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose()
          const m = obj.material
          if (Array.isArray(m)) m.forEach((x) => x.dispose())
          else {
            if (m instanceof THREE.MeshBasicMaterial && m.map) m.map.dispose()
            m.dispose()
          }
        }
      })
    },
  }
}
